import { getHighestNumber } from '@/util/number';
import { sortSubroutines } from '@/util/sort';
import IconButton from '@/components/common/IconButton';
import { useApplication } from '@/hooks/useApplication';
import { useSubroutine } from '@/hooks/useSubroutine';
import { Signal } from '@/types/signal';
import { Subroutine } from '@/types/subroutine';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { FC, useEffect, useState } from 'react';

type SignalListItemEditProps = {
  signal?: Signal;
  onClose: (isEmpty?: boolean) => void;
  onSave: (newSignal: Signal) => void;
};

const SignalListItemEdit: FC<SignalListItemEditProps> = (props) => {
  const { application } = useApplication();
  const { sortSubroutinesBy } = useSubroutine();

  const [signalName, setSignalName] = useState(props.signal?.name || '');
  const [signalDescription, setSignalDescription] = useState(props.signal?.description || '');
  const [subStart, setSubStart] = useState(props.signal?.substart === -1 ? undefined : props.signal?.substart);
  const [signalSubroutines, setSignalSubroutines] = useState<Subroutine[]>();
  const isError = signalName.startsWith('_debug');
  const valid: boolean = !!signalName && !isError;

  const handleClose = (isEmpty?: boolean) => {
    setSignalName('');
    setSignalDescription('');
    setSubStart(undefined);
    props.onClose(isEmpty);
  };

  const generateNewId = (signals: Signal[]): number => {
    return getHighestNumber(signals, 'id') + 1;
  };

  useEffect(() => {
    setSignalSubroutines(
      sortSubroutines(
        sortSubroutinesBy,
        application.subroutines.filter(
          (subroutine) =>
            subroutine.startblock && application.blocks.nodes[subroutine.startblock]?.type === 'SignalStart'
        )
      )
    );
  }, [application.subroutines]);

  return (
    <Stack
      direction='row'
      gap={1}
      pt={1}
      width='100%'
      alignItems='flex-start'
      alignContent='flex-start'
      bgcolor='action.focus'
      borderRadius={2}
      p={1}
    >
      {props.signal?.id && <code>{props.signal.id}</code>}
      <Stack gap={1} flexGrow={2} width='100%'>
        <TextField
          label='Signal Name'
          value={signalName}
          onChange={(event) => setSignalName(event.currentTarget.value.trimStart())}
          fullWidth
          size='small'
          autoFocus
          error={isError}
          helperText={isError ? "Names cannot start with '_debug'" : ''}
          required
          InputProps={{ autoComplete: 'off' }}
        />
        <TextField
          label='Signal Description'
          value={signalDescription}
          onChange={(event) => setSignalDescription(event.currentTarget.value)}
          fullWidth
          size='small'
          multiline
          minRows={2}
          InputProps={{ autoComplete: 'off' }}
        />
      </Stack>
      <FormControl fullWidth size='small' sx={{ flexGrow: 1 }}>
        <InputLabel id='variable-type-select-label'>Signal handler</InputLabel>
        <Select
          labelId='variable-type-select-label'
          id='variable-type-select'
          value={subStart}
          label='Variable type'
          onChange={(event) => setSubStart(event.target.value as number)}
        >
          <MenuItem key={`valuetype.none.-1`} value={undefined}>
            &#8203; {/* Invisible character to keep normal MenuItem height */}
          </MenuItem>
          {signalSubroutines?.map((subroutine, index) => (
            <MenuItem key={`valuetype.${subroutine.id}.${index}`} value={subroutine.id}>
              {subroutine.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        tooltip={valid ? 'Save' : undefined}
        disabled={!valid}
        onClick={() => {
          if (!valid) return;
          const newSignal: Signal = {
            id: props.signal?.id || generateNewId(application.signals || []),
            name: signalName,
            description: signalDescription,
            substart: subStart ?? 0
          };
          props.onSave(newSignal);
          handleClose();
        }}
      >
        <SaveIcon />
      </IconButton>
      <IconButton tooltip='Cancel' onClick={() => handleClose(!props.signal?.name)}>
        <CancelIcon />
      </IconButton>
    </Stack>
  );
};

export default SignalListItemEdit;
